import { Box, Typography, Button, SxProps, Theme } from '@mui/material';
import styles from './Statistics.module.scss';
import { LucideIcon } from 'lucide-react';

interface SubItem {
  label: string;
  value: string | number;
}

interface StatCardProps {
  title: string;
  value?: string | number;
  currency?: string;
  icon: LucideIcon;
  subItems?: SubItem[];
  buttonConfig?: {
    label: string;
    color: 'green' | 'orange' | 'red' | 'cyan' | 'blue' | 'magenta';
  };
  isLarge?: boolean;
  className?: string;
  sx?: SxProps<Theme>;
  children?: React.ReactNode;
  onButtonClick?: () => void;
}

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  currency = '$',
  icon: Icon,
  subItems,
  buttonConfig,
  isLarge = false,
  className = '',
  sx,
  children,
  onButtonClick,
}) => {
  const isNegative =
    typeof value === 'number' ? value < 0 : typeof value === 'string' && value.startsWith('-');
  const displayValue = isNegative
    ? typeof value === 'string'
      ? value.substring(1)
      : Math.abs(value as number)
    : value;

  return (
    <Box
      className={`${styles.statCard} ${isLarge ? styles.largeCard : ''} ${className}`}
      sx={{
        gridColumn: isLarge ? '1 / -1' : 'auto',
        ...sx,
      }}
    >
      <Box className={styles.mainContent}>
        <Box className={styles.cardHeader}>
          <Box className={styles.iconWrapper}>
            <Icon size={isLarge ? 32 : 24} />
          </Box>
          <Box className={styles.titleWrapper}>
            <Typography className={styles.cardTitle}>{title}</Typography>
            {value !== undefined && value !== null && (
              <Typography
                className={styles.cardValue}
                sx={{ color: isNegative ? '#ff4d4f' : undefined }}
              >
                {isNegative ? '-' : ''}
                {currency}
                {displayValue}
              </Typography>
            )}
          </Box>
        </Box>

        {subItems && (
          <Box className={styles.subItems}>
            {subItems.map((item, index) => (
              <Box key={index} className={styles.subItem}>
                <Typography className={styles.subLabel}>{item.label}:</Typography>
                <Typography className={styles.subValue}>{item.value}</Typography>
              </Box>
            ))}
          </Box>
        )}

        {buttonConfig && (
          <Button
            variant="contained"
            className={`${styles.cardActionBtn} ${styles[buttonConfig.color]}`}
            onClick={onButtonClick}
          >
            {buttonConfig.label}
          </Button>
        )}
      </Box>

      {children && <Box className={styles.extraContent}>{children}</Box>}
    </Box>
  );
};

export default StatCard;
